import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { MenuLink } from '@/components/menu-link';
import { NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { InvoiceMoneyBar } from '@/components/sales/invoice-money-bar';
import { SalesTable } from '@/components/sales/sales-table';
import { useEInvoiceMap } from '@/lib/einvoicing';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { PERIODS, periodStart } from '@/lib/dates';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useSalesTransactions, type SalesRow } from '@/lib/sales';

export const Route = createFileRoute('/sales/all')({ component: AllSales });

const TYPES = ['all', 'invoice', 'quote', 'credit_note', 'sales_receipt', 'payment'] as const;
const STATUSES: Array<
  ['all' | 'open' | 'overdue' | 'draft' | 'paid' | 'void', (r: SalesRow) => boolean]
> = [
  ['all', () => true],
  ['open', (r) => ['approved', 'partially_paid', 'pending', 'accepted'].includes(r.status)],
  ['overdue', (r) => r.overdue],
  ['draft', (r) => r.status === 'draft'],
  ['paid', (r) => ['paid', 'posted', 'converted'].includes(r.status)],
  ['void', (r) => r.status === 'void'],
];
const STATUS_KEY = {
  all: 'salesPages.allSales.allStatuses',
  open: 'salesPages.status.open',
  overdue: 'salesPages.status.overdue',
  draft: 'salesPages.status.draft',
  paid: 'salesPages.allSales.paidClosed',
  void: 'salesPages.status.void',
} as const;

/** All sales (document 06 S2): every sales document and payment with filters. */
function AllSales() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const tx = useSalesTransactions();
  const einvoices = useEInvoiceMap();
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [period, setPeriod] = useState('all');
  const [customer, setCustomer] = useState('all');
  const [q, setQ] = useState('');
  const [bar, setBar] = useState<string | null>(null);
  const customers = useMemo(
    () =>
      [...new Map((tx.data ?? []).map((r) => [r.contactId, r.contactName])).entries()].sort(
        (a, b) => a[1].localeCompare(b[1]),
      ),
    [tx.data],
  );
  const since = periodStart(period);
  const needle = q.trim().toLowerCase();
  const statusFn = STATUSES.find((s) => s[0] === status)![1];
  const rows = (tx.data ?? []).filter(
    (r) =>
      (type === 'all' || r.type === type) &&
      statusFn(r) &&
      (!since || r.date >= since) &&
      (customer === 'all' || r.contactId === customer) &&
      (!bar ||
        (r.type === 'invoice' &&
          ['approved', 'partially_paid'].includes(r.status) &&
          (bar === 'overdue' ? r.overdue : bar === 'not_due' ? !r.overdue : false))) &&
      (!needle ||
        r.contactName.toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle) ||
        (r.memo ?? '').toLowerCase().includes(needle)),
  );
  return (
    <>
      <PageHeader
        title={t('salesPages.allSales.title')}
        actions={
          me.data?.isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1">
                  {t('salesPages.salesListCommon.newTransaction')}{' '}
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <MenuLink to="/sales/invoices/new">{t('salesPages.kind.invoice')}</MenuLink>
                <MenuLink to="/sales/receive-payment/new">{t('salesPages.kind.payment')}</MenuLink>
                <MenuLink to="/sales/quotes/new">{t('salesPages.kind.quote')}</MenuLink>
                <MenuLink to="/sales/credit-notes/new">{t('salesPages.kind.credit_note')}</MenuLink>
                <MenuLink to="/sales/sales-receipts/new">
                  {t('salesPages.kind.sales_receipt')}
                </MenuLink>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : (
        <>
          <InvoiceMoneyBar active={bar} onSelect={setBar} />
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <NativeSelect
              aria-label={t('salesPages.allSales.typeLabel')}
              className="w-[180px] bg-card"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {TYPES.map((v) => (
                <option key={v} value={v}>
                  {t(`salesPages.allSales.types.${v}`)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('salesPages.salesListCommon.statusLabel')}
              className="w-[160px] bg-card"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map(([v]) => (
                <option key={v} value={v}>
                  {t(STATUS_KEY[v])}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('salesPages.salesListCommon.dateLabel')}
              className="w-[160px] bg-card"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map(([v]) => (
                <option key={v} value={v}>
                  {t(`purchasePages.listCommon.dateFilter.${v as '30'}`)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('salesPages.allSales.customerLabel')}
              className="w-[220px] bg-card"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
            >
              <option value="all">{t('salesPages.allSales.allCustomers')}</option>
              {customers.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </NativeSelect>
            <div className="relative ms-auto w-[280px]">
              <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card ps-8"
                placeholder={t('salesPages.allSales.searchPlaceholder')}
                aria-label={t('salesPages.allSales.searchLabel')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : tx.isError ? (
            <EmptyState title={t('salesPages.allSales.loadError')} hint={tx.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={
                tx.data.length
                  ? t('salesPages.allSales.noMatch')
                  : t('salesPages.salesListCommon.noSalesYet')
              }
              action={
                me.data?.isStaff ? (
                  <Button asChild>
                    <Link to="/sales/invoices/new">
                      {t('salesPages.salesListCommon.newInvoice')}
                    </Link>
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <SalesTable rows={rows} einvoices={einvoices} batch />
          )}
        </>
      )}
    </>
  );
}
